/**
 * Phase 261e — Active Workspace Sport sidebar widget.
 *
 * Renders the academy's CONFIGURED prominent sport (from MongoDB via
 * /api/academy/sport-anchor + sports_registry.py) in the AppShell sidebar.
 *
 * Strictly honest:
 *   • Header reads "Active Workspace Sport" — NEVER "AI Detected".
 *   • No confidence scores. No fake ML. No `random.choice()`.
 *   • Data flows: MongoDB academy.sport → /api/academy/sport-anchor →
 *     useSportConfig(key) → registry config.
 *   • Hidden for students/parents (their roles return null sportKey).
 *   • Collapses gracefully when sidebar is in icon-only mode.
 */
import { Activity } from 'lucide-react';
import { useAcademySport } from '@/hooks/useAcademySport';

export default function ActiveSportSidebarWidget({ collapsed = false }) {
  const { sportKey, sportConfig, loading } = useAcademySport();

  // Students / parents / unknown — render nothing.
  if (!sportKey) return null;

  // Loading skeleton (very short — fetch is single shot)
  if (loading || !sportConfig) {
    return (
      <div
        data-testid="active-sport-widget-loading"
        className={`mx-2 my-2 rounded-lg border border-border bg-muted/30 animate-pulse ${
          collapsed ? 'h-8' : 'h-16'
        }`}
      />
    );
  }

  const surfaceLabel = sportConfig.surface_label || 'Pitch';
  const primaryMetric = sportConfig.primary_metric?.label || sportConfig.metric_fields?.[0]?.label || '';
  const secondaryMetric = sportConfig.secondary_metric?.label || sportConfig.metric_fields?.[1]?.label || '';
  const emoji = sportConfig.emoji || '⚽';
  const displayName = sportConfig.display_name || sportKey;

  // Collapsed (icon-only) sidebar — just show the emoji with a tooltip.
  if (collapsed) {
    return (
      <div
        data-testid="active-sport-widget"
        data-collapsed="true"
        title={`Active workspace sport: ${displayName} (${surfaceLabel})`}
        className="mx-2 my-2 flex items-center justify-center h-8 rounded-lg border border-border bg-muted/30 text-lg select-none cursor-default"
      >
        <span aria-label={displayName}>{emoji}</span>
      </div>
    );
  }

  // Expanded sidebar — full pill with sport name, surface, and metric chips.
  return (
    <div
      data-testid="active-sport-widget"
      data-collapsed="false"
      data-sport={sportKey}
      className="mx-2 my-2 rounded-lg border border-border bg-muted/30 p-2.5 space-y-1.5"
    >
      <div className="text-[9px] uppercase font-bold tracking-wider text-muted-foreground/80 flex items-center gap-1">
        <Activity className="h-3 w-3 text-emerald-600" /> Active Workspace Sport
      </div>
      <div className="text-xs font-bold text-foreground flex items-center gap-1.5">
        <span aria-hidden="true">{emoji}</span>
        <span className="truncate" data-testid="active-sport-widget-name">{displayName}</span>
      </div>
      <div className="flex flex-wrap gap-1 text-[10px] text-muted-foreground font-medium">
        <span
          className="bg-background border border-border px-1.5 py-0.5 rounded-md"
          data-testid="active-sport-widget-surface"
        >
          Surface: {surfaceLabel}
        </span>
        {primaryMetric && (
          <span
            className="bg-background border border-border px-1.5 py-0.5 rounded-md"
            data-testid="active-sport-widget-metric-primary"
          >
            {primaryMetric}
          </span>
        )}
        {secondaryMetric && (
          <span
            className="bg-background border border-border px-1.5 py-0.5 rounded-md"
            data-testid="active-sport-widget-metric-secondary"
          >
            {secondaryMetric}
          </span>
        )}
      </div>
    </div>
  );
}
